import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { ProductPlaceholder } from "../components/ProductPlaceholder";
import { ColourSwatch } from "../components/ColourSwatch";
import { EnquiryDialog } from "../components/EnquiryDialog";
import { Button } from "../components/ui/button";
import { MessageCircle, Phone, GitCompare, ChevronRight, Loader2 } from "lucide-react";
import { imageUrl, whatsappLink, PHONE_TEL, PHONE_DISPLAY } from "../data/constants";
import { useCompare } from "../context/CompareContext";
import api from "../lib/api";

const SPEC_ROWS = [
  ["Category", "category"], ["Material", "material"], ["Construction", "construction"],
  ["Thickness", "thickness"], ["Weight", "weight"], ["Backing", "backing"], ["Pack Size", "pack_size"],
];

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [palette, setPalette] = useState({});
  const [activeImg, setActiveImg] = useState(0);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toggle, has } = useCompare();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(null)).finally(() => setLoading(false));
    api.get("/colours").then((r) => { const p = {}; r.data.forEach((c) => (p[c.name] = c.hex)); setPalette(p); }).catch(() => {});
  }, [id]);

  const images = useMemo(() => (product?.images || []).map((p) => imageUrl(p, 1100)).filter(Boolean), [product]);

  if (loading) return <Layout><div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#10B981]" /></div></Layout>;
  if (!product) return <Layout><div className="mx-auto max-w-3xl px-4 py-24 text-center"><h1 className="font-display text-2xl font-bold">Product not found</h1><Link to="/catalogue" className="mt-4 inline-block text-[#059669] hover:underline">← Back to catalogue</Link></div></Layout>;

  const specRows = SPEC_ROWS.filter(([, key]) => product[key]);

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="product-detail-container">
        <nav className="mb-6 flex items-center gap-1.5 text-sm text-slate-500">
          <Link to="/" className="hover:text-[#059669]">Home</Link><ChevronRight className="h-4 w-4" />
          <Link to="/catalogue" className="hover:text-[#059669]">Products</Link><ChevronRight className="h-4 w-4" />
          <Link to={`/catalogue?category=${encodeURIComponent(product.category)}`} className="hover:text-[#059669]">{product.category}</Link>
          <ChevronRight className="h-4 w-4" /><span className="font-medium text-slate-700">{product.name}</span>
        </nav>

        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <div className="aspect-square overflow-hidden rounded-3xl border border-slate-200 bg-slate-100">
              {images.length > 0 ? (
                <img src={images[activeImg]} alt={product.name} data-testid="product-gallery-main-image"
                  className="h-full w-full object-cover" />
              ) : (
                <ProductPlaceholder className="h-full w-full" />
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto scrollbar-hide">
                {images.map((src, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 ${activeImg === i ? "border-[#10B981]" : "border-slate-200"}`}>
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <span className="inline-block rounded-full bg-[#0B1B3D] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
              {product.category}
            </span>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#0B1B3D] sm:text-4xl">{product.name}</h1>
            {product.description && <p className="mt-3 text-slate-600">{product.description}</p>}

            {product.sizes?.length > 0 && (
              <div className="mt-6">
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-slate-900">Available Sizes</h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <span key={s} className="rounded-lg bg-slate-100 px-3 py-1.5 font-spec text-xs font-medium text-slate-700">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {product.colours?.length > 0 && (
              <div className="mt-6">
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-slate-900">Colours</h4>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  {product.colours.map((c) => (
                    <span key={c} className="flex items-center gap-2 rounded-full bg-slate-100 py-1 pl-1 pr-3 text-sm font-medium text-slate-700">
                      <ColourSwatch name={c} palette={palette} size="md" /> {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {specRows.length > 0 && (
              <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200" data-testid="product-specifications-table">
                <table className="w-full text-sm">
                  <tbody>
                    {specRows.map(([label, key], i) => (
                      <tr key={key} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                        <td className="px-4 py-2.5 font-semibold text-slate-700">{label}</td>
                        <td className="px-4 py-2.5 font-spec text-slate-600">{product[key]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {product.features?.length > 0 && (
              <div className="mt-6">
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-slate-900">Features</h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {product.features.map((f) => (
                    <span key={f} className="rounded-full bg-[#ECFDF5] px-3 py-1 text-xs font-medium text-[#059669] ring-1 ring-[#A7F3D0]">{f}</span>
                  ))}
                </div>
              </div>
            )}

            {product.applications?.length > 0 && (
              <div className="mt-6">
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-slate-900">Applications</h4>
                <p className="mt-2 text-slate-600">{product.applications.join(" · ")}</p>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => setQuoteOpen(true)} data-testid="product-detail-quote-btn"
                className="h-12 flex-1 bg-[#0B1B3D] font-bold hover:bg-[#1E293B] sm:flex-none sm:px-8">
                Get Wholesale Quote
              </Button>
              <a href={whatsappLink({ product: product.name, size: product.sizes?.[0], colour: product.colours?.[0] })}
                target="_blank" rel="noopener noreferrer" data-testid="product-detail-whatsapp-btn"
                className="flex h-12 items-center gap-2 rounded-xl bg-[#25D366] px-5 font-bold text-white transition-transform hover:scale-105">
                <MessageCircle className="h-5 w-5" /> WhatsApp
              </a>
              <a href={`tel:${PHONE_TEL}`} data-testid="product-detail-call-btn"
                className="flex h-12 items-center gap-2 rounded-xl border border-slate-200 px-5 font-bold text-[#0B1B3D] hover:border-[#10B981]">
                <Phone className="h-5 w-5" /> Call
              </a>
            </div>

            <button onClick={() => toggle(product)} data-testid="product-detail-add-compare-btn"
              className={`mt-4 flex items-center gap-2 text-sm font-semibold ${has(product.id) ? "text-[#059669]" : "text-slate-500 hover:text-[#059669]"}`}>
              <GitCompare className="h-4 w-4" /> {has(product.id) ? "Added to compare" : "Add to compare"}
            </button>

            <p className="mt-6 font-spec text-xs text-slate-400">Call us at {PHONE_DISPLAY} for bulk pricing.</p>
          </div>
        </div>
      </div>
      <EnquiryDialog open={quoteOpen} onOpenChange={setQuoteOpen} product={product} />
    </Layout>
  );
}
